import { useMemo } from 'react';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { api, type ListTasksQuery, type TaskStatusResponse } from '@/api/api';

export const taskKeys = {
  all: ['tasks'] as const,
  list: (projectId: string, query: ListTasksQuery = {}) =>
    [...taskKeys.all, 'list', projectId, query] as const,
  byEpic: (epicId: string) => [...taskKeys.all, 'epic', epicId] as const,
  detail: (taskId: string) => [...taskKeys.all, 'detail', taskId] as const,
  riskCallout: (taskId: string) => [...taskKeys.all, 'risk-callout', taskId] as const,
  riskCalloutHistory: (taskId: string) =>
    [...taskKeys.all, 'risk-callout-history', taskId] as const,
};

export const tasksListOptions = (projectId: string, query: ListTasksQuery = {}) =>
  queryOptions({
    queryKey: taskKeys.list(projectId, query),
    queryFn: () => api.listProjectTasks(projectId, query),
  });

export const epicTasksOptions = (epicId: string) =>
  queryOptions({
    queryKey: taskKeys.byEpic(epicId),
    queryFn: () => api.listEpicTasks(epicId),
  });

export const taskStatusKeys = {
  all: ['task-statuses'] as const,
  byProject: (projectId: string) => [...taskStatusKeys.all, 'project', projectId] as const,
};

export const taskStatusOptions = (projectId: string) =>
  queryOptions({
    queryKey: taskStatusKeys.byProject(projectId),
    queryFn: () => api.listTaskStatuses(projectId),
    staleTime: 60_000,
  });

/**
 * Fetches the project's task statuses and returns both the sorted array
 * and a Map<id, TaskStatusResponse> for O(1) lookups.
 */
export function useTaskStatuses(projectId: string) {
  const { data = [] } = useQuery(taskStatusOptions(projectId));
  const statusesById = useMemo(
    () => new Map(data.map((s) => [s.id, s])),
    [data],
  );
  return { statuses: data, statusesById } as const;
}

export const taskDetailOptions = (taskId: string) =>
  queryOptions({
    queryKey: taskKeys.detail(taskId),
    queryFn: () => api.getTask(taskId),
    staleTime: 30_000,
  });

export const taskRiskCalloutOptions = (taskId: string) =>
  queryOptions({
    queryKey: taskKeys.riskCallout(taskId),
    queryFn: () => api.getTaskRiskCallout(taskId),
  });

export const taskRiskCalloutHistoryOptions = (taskId: string) =>
  queryOptions({
    queryKey: taskKeys.riskCalloutHistory(taskId),
    queryFn: () => api.getTaskRiskCalloutHistory(taskId),
  });

export type { TaskStatusResponse };
